import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as echarts from 'echarts'
import guangzhouGeo from '../../dashboard/modal/guangzhou.geo.json'
import { POINTS } from '../../dashboard/modal/config'

// 高德地图 Key
// 建议：生产环境通过 env 或后端下发，不要在前端源码明文保存
const AMAP_KEY = '9a770993c1ebb1199d925f4ac6ce3ab1'

/**
 * 热力图可视化组件 - 使用高德地图底图
 * 
 * 功能说明：
 * - 使用高德地图（AMap JS API）作为底图，并在上层使用 ECharts 绘制广州市区域热力图
 * - 动态加载 AMap 脚本（无需在全站模版中手动添加）
 * - 在 map 容器之上叠加一个绝对定位的 echarts 容器
 * - 将经纬度通过 AMap 的 lngLatToContainer 方法投影为像素坐标，并用 echarts 的 cartesian2d 绘制
 * - 监听地图的 move/zoom/resize 事件，实时重投影并更新 echarts 数据
 * - 根据 guangzhou.geo.json 自动绘制广州市11个区的边界线和区域标签
 *
 * 特色功能：
 * - 支持 Shift + 拖拽进行矩形框选缩放
 * - 动态绘制广州市各区域边界线和标签
 * - 根据热力值智能显示不同颜色和大小的散点
 * - 热力参数控制面板（半径、透明度、最大值）
 *
 * 注意：此实现避免依赖第三方 echarts-amap 插件，便于部署与版本兼容。
 */
// 组件职责：
// 1) 动态加载 AMap 并初始化地图
// 2) 解析 guangzhou.geo.json 绘制行政区边界与标签
// 3) 在地图上叠加 ECharts，投影经纬度为像素坐标绘制点
// 4) 处理交互（缩放、拖拽、Shift+拉框缩放）与资源清理
// 一键切换：true 用 Redux 数据，false 用 config.POINTS（便于排查问题）
const USE_REDUX_DATA = false
const EMPTY_POINTS = []

/**
 * @param {object} props
 * @param {string} [props.mapStyle] 高德底图样式，默认浅色；深色可传 `amap://styles/dark` 或 `amap://styles/darkblue`（极夜蓝）
 * @param {'default'|'dark'} [props.mapTheme] 为 `dark` 时使用 HUD 同色底 + 瓦片压光兜底（部分 Key 下官方深色样式不生效）
 * @param {string} [props.embedBackground] 外层容器背景色；未传时由 mapTheme / mapStyle 推断
 * @param {number} [props.mapCanvasOpacity] 地图画布透明度；未传时深色为 1、浅色为 0.6（与历史表现一致）
 */
const HeatMapVisualization = ({
  mapStyle: mapStyleProp = 'amap://styles/light',
  mapTheme = 'default',
  embedBackground: embedBackgroundProp,
  mapCanvasOpacity: mapCanvasOpacityProp,
}) => {
  const isDarkHud = mapTheme === 'dark'
  const embedBackground =
    embedBackgroundProp ??
    (isDarkHud
      ? '#060a10'
      : String(mapStyleProp).includes('dark')
        ? 'transparent'
        : '#ffffff')
  const mapCanvasOpacity =
    mapCanvasOpacityProp ?? (isDarkHud || String(mapStyleProp).includes('dark') ? 1 : 0.6)

  const reduxPoints = useSelector((state) => {
    return state.order?.order?.orderListPage?.data?.records ?? EMPTY_POINTS
  })
  const points = USE_REDUX_DATA ? reduxPoints : POINTS
  const mapContainerRef = useRef(null)
  const overlayRef = useRef(null)
  const mapRef = useRef(null)
  const chartRef = useRef(null)
  const polygonRef = useRef(null)
  const heatmapRef = useRef(null)
  const markersRef = useRef([])
  const clusterRef = useRef(null)
  const heatOptionsRef = useRef({ radius: 48, opacityMax: 0.9, max: 100 })
  const pointsRef = useRef([])
  
  // 应用热力配置到实例
  const applyHeatOptions = (next = {}) => {
    heatOptionsRef.current = { ...heatOptionsRef.current, ...next }
    try {
      const heat = heatmapRef.current
      if (!heat) return
      const { radius, opacityMax } = heatOptionsRef.current
      if (typeof heat.setOptions === 'function') {
        heat.setOptions({ radius, opacity: [0, opacityMax] })
      }
      // 重置数据以应用新的 max
      const pts = Array.isArray(pointsRef.current) ? pointsRef.current : []
      const data = pts.map((p) => ({
        lng: Array.isArray(p?.coord) ? p.coord[0] : p?.lng || 0,
        lat: Array.isArray(p?.coord) ? p.coord[1] : p?.lat || 0,
        count: Number(p?.value) || 0
      }))
      const { max } = heatOptionsRef.current
      if (typeof heat.setDataSet === 'function') {
        heat.setDataSet({ data, max })
      }
    } catch (e) {
      // ignore
    }
  }

  // 渲染模式：使用点聚合
  const RENDER_MODE = 'cluster'

  useEffect(() => {
    let isUnmounted = false
    let observer = null

    // 动态引入 AMap JSAPI（若 window.AMap 已存在则复用）
    const loadAmap = () => new Promise((resolve, reject) => {
      if (window.AMap) return resolve(window.AMap)
      const script = document.createElement('script')
      script.src = `https://webapi.amap.com/maps?v=2.0&key=${AMAP_KEY}`
      script.async = true
      script.onload = () => resolve(window.AMap)
      script.onerror = reject
      document.head.appendChild(script)
    })

    const init = async () => {
      try {
        const AMap = await loadAmap()
        if (isUnmounted) return

        // 创建地图并将视角聚焦到广州市中心（可查看所有区域）
        const center = [113.25, 23.13]
        const map = new window.AMap.Map(mapContainerRef.current, {
          center, // 地图中心点坐标 [经度, 纬度]，广州市中心位置
          zoom: 12.9, // 初始缩放级别，数值越大地图越放大（显示范围越小，细节越多）
          viewMode:'2D', // 视图模式：'2D' 二维地图，'3D' 三维地图
          pitch: 0, // 俯仰角度（3D模式下有效），0 表示水平视角
          rotation: 0, // 地图旋转角度，0 表示正北方向
          animateEnable: false, // 是否启用地图动画效果，false 表示禁用动画以提升性能
          zooms:[4,17], // 允许的缩放级别范围 [最小值, 最大值]
          mapStyle: mapStyleProp, // 默认浅色；世界指挥中心等业务可传入 amap://styles/dark
          features: ['bg', 'road'], // 'bg' 底图背景，'road' 道路，'building' 建筑物，'point' 兴趣点等
          showLabel: false, // 是否显示地图标签（地名、POI等），false 表示不显示标签
          dragEnable: true, // 是否允许拖拽地图，true 表示可以拖拽移动地图
          zoomEnable: true, // 是否允许缩放地图，true 表示可以通过滚轮、手势等方式缩放
          doubleClickZoom: false, // 是否允许双击缩放，false 表示禁用双击缩放功能
          rotateEnable: false, // 是否允许旋转地图，false 表示禁用旋转功能
          pitchEnable: false, // 是否允许俯仰操作（3D模式下），false 表示禁用俯仰功能
          keyboardEnable: false, // 是否允许键盘操作（方向键、+/- 键等），false 表示禁用键盘控制
          touchZoomCenter: 1 // 触摸缩放中心点，1 表示以触摸点为中心缩放，0 表示以地图中心缩放
        })
        mapRef.current = map

        // 部分环境下仅构造函数里的 mapStyle 不生效，complete 后再应用一次（高德官方建议亦支持 setMapStyle）
        const applyMapStyle = () => {
          try {
            if (typeof map.setMapStyle === 'function') {
              map.setMapStyle(mapStyleProp)
            }
          } catch (e) {
            // ignore
          }
        }
        applyMapStyle()
        map.on('complete', applyMapStyle)

        // 兜底处理：运行时再次尝试关闭默认标注
        try {
          if (typeof map.setFeatures === 'function') {
            map.setFeatures(['bg', 'road'])
          }
        } catch (e) {
          // ignore
        }

        // 勾勒广州市各区域边界（使用本地 GeoJSON）
        try {
          const featureList = Array.isArray(guangzhouGeo?.features) ? guangzhouGeo.features : []
          const polygons = []
          
          if (featureList.length) {
            featureList.forEach((feature) => {
              const geometry = feature?.geometry
              if (!geometry || !geometry.coordinates) return
              
              const districtName = feature.properties?.name || '未知区域'
              const center = feature.properties?.center || [113.3, 23.1] // 标签位置：若 GeoJSON 提供 center 则优先使用
              
              // 将二维/三维数组坐标转换为 AMap 可识别的路径
              const toLngLatPath = (coords) =>
                Array.isArray(coords)
                  ? coords
                    .filter((point) => Array.isArray(point) && point.length >= 2)
                    .map(([lng, lat]) => new window.AMap.LngLat(lng, lat))
                  : []
              
              let boundaryPaths = []
              
              if (geometry.type === 'Polygon') {
                geometry.coordinates.forEach((ring) => {
                  const path = toLngLatPath(ring)
                  if (path.length > 2) boundaryPaths.push(path)
                })
              } else if (geometry.type === 'MultiPolygon') {
                geometry.coordinates.forEach((poly) => {
                  poly.forEach((ring) => {
                    const path = toLngLatPath(ring)
                    if (path.length > 2) boundaryPaths.push(path)
                  })
                })
              }
              
              // 为每个区域创建边界多边形，使用更亮更突出的样式
              if (boundaryPaths.length > 0) {
                const polygon = new window.AMap.Polygon({
                  // GeoJSON 提取的多边形边界集合：每个子数组代表一个闭合环路径
                  path: boundaryPaths,
                  strokeColor: '#29b6f6',
                  strokeWeight: 5,
                  strokeOpacity: 0.95,
                  fillColor: 'rgba(41, 182, 246, 0.15)',
                  fillOpacity: 0.3,
                  zIndex: 90,
                  cursor: 'pointer',
                  extData: {
                    name: districtName
                  }
                })
                
                // 添加鼠标悬停效果
                polygon.on('mouseover', function() {
                  this.setOptions({
                    fillColor: 'rgba(41, 182, 246, 0.25)',
                    strokeWeight: 4
                  })
                })
                polygon.on('mouseout', function() {
                  this.setOptions({
                    fillColor: 'rgba(41, 182, 246, 0.15)',
                    strokeWeight: 5
                  })
                })
                
                map.add(polygon)
                
                // 添加区域标签（使用 Marker 配合自定义内容）
                const labelDiv = document.createElement('div')
                labelDiv.innerHTML = districtName
                labelDiv.style.cssText = `
                  font-size: 12px;
                  font-weight: bold;
                  color: #aee2ff;
                  background-color: rgba(5, 28, 43, 0.95);
                  border: 1px solid rgba(79, 195, 247, 0.7);
                  border-radius: 4px;
                  padding: 4px 8px;
                  white-space: nowrap;
                  text-shadow: 0 0 6px rgba(41, 182, 246, 0.8);
                  box-shadow: 0 2px 12px rgba(41, 182, 246, 0.4);
                `
                
                const textMarker = new window.AMap.Marker({
                  position: center,
                  content: labelDiv,
                  offset: new window.AMap.Pixel(0, -10),
                  zIndex: 91
                })
                
                map.add(textMarker)
                
                polygons.push({ polygon, textMarker })
              }
            })
            
            // 保存所有多边形引用以便清理
            polygonRef.current = polygons
          }
        } catch (boundaryErr) {
          console.error('绘制区域边界失败:', boundaryErr)
        }

        // 初始化 ECharts 覆盖层：使用 overlayRef 作为画布，叠加在地图之上
        const chart = echarts.init(overlayRef.current, null, { renderer: 'canvas' })
        chartRef.current = chart

        // 使用 Redux 订单热力图数据（fetchOrdersByType dataType: 'heatMap'）
        pointsRef.current = points

        if (RENDER_MODE === 'heat') {
          // AMap 原生热力图图层（视觉融合）
          try {
            if (window.AMap && typeof window.AMap.plugin === 'function') {
              window.AMap.plugin(['AMap.HeatMap'], () => {
                try {
                  const heatmap = new window.AMap.HeatMap(map, {
                    radius: heatOptionsRef.current.radius,
                    opacity: [0, heatOptionsRef.current.opacityMax],
                    zooms: [3, 20],
                    gradient: {
                      0.2: '#1e88e5',
                      0.4: '#43a047',
                      0.6: '#fdd835',
                      0.8: '#fb8c00',
                      1.0: '#e53935'
                    }
                  })

                  const data = Array.isArray(points)
                    ? points.map((p) => ({
                        lng: Array.isArray(p?.coord) ? p.coord[0] : p?.lng || 0,
                        lat: Array.isArray(p?.coord) ? p.coord[1] : p?.lat || 0,
                        count: Number(p?.value) || 0
                      }))
                    : []

                  heatmap.setDataSet({ data, max: heatOptionsRef.current.max })
                  heatmapRef.current = heatmap
                } catch (e) {
                  // ignore heatmap init error
                }
              })
            }
          } catch (e) {
            // ignore
          }
        } else if (RENDER_MODE === 'cluster') {
          // 点聚合：将点转为 Marker，并使用 MarkerCluster/MarkerClusterer 聚合
          try {
            // 先合并相同坐标的点，累加权重值
            const pointMap = new Map()
            if (Array.isArray(points)) {
              points.forEach((p) => {
                const lng = Array.isArray(p?.coord) ? p.coord[0] : p?.lng || 0
                const lat = Array.isArray(p?.coord) ? p.coord[1] : p?.lat || 0
                const key = `${lng.toFixed(6)}_${lat.toFixed(6)}` // 使用6位小数精度作为key
                const value = Number(p?.value) || 0
                
                if (pointMap.has(key)) {
                  const existing = pointMap.get(key)
                  existing.count += 1
                  existing.totalValue += value
                  existing.names.push(p?.name || '')
                } else {
                  pointMap.set(key, {
                    lng,
                    lat,
                    count: 1,
                    totalValue: value,
                    names: [p?.name || '']
                  })
                }
              })
            }
            
            // 将合并后的点转为 Marker，添加增强的脉冲动效
            const markers = Array.from(pointMap.values()).map((point, index) => {
              // 为每个点添加随机延迟，让动画更自然
              const delay = (index % 10) * 0.2
              const markerContent = `
                <div style="position: relative; width: 14px; height: 14px;">
                  <!-- 主点 -->
                  <div style="
                    position: absolute;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: rgba(255,107,67,0.95);
                    box-shadow: 0 0 12px rgba(255,107,67,0.9), 0 0 20px rgba(255,107,67,0.5);
                    animation: heatmap-pulse 1.5s ease-in-out infinite;
                    animation-delay: ${delay}s;
                  "></div>
                  <!-- 第一层脉冲环 -->
                  <div style="
                    position: absolute;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: rgba(255,107,67,0.7);
                    animation: heatmap-pulse-ring-1 1.5s ease-out infinite;
                    animation-delay: ${delay}s;
                  "></div>
                  <!-- 第二层脉冲环 -->
                  <div style="
                    position: absolute;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: rgba(255,107,67,0.5);
                    animation: heatmap-pulse-ring-2 1.5s ease-out infinite;
                    animation-delay: ${delay + 0.3}s;
                  "></div>
                  <!-- 第三层脉冲环 -->
                  <div style="
                    position: absolute;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: rgba(255,107,67,0.3);
                    animation: heatmap-pulse-ring-3 1.5s ease-out infinite;
                    animation-delay: ${delay + 0.6}s;
                  "></div>
                </div>
              `
              
              const marker = new window.AMap.Marker({
                position: [point.lng, point.lat],
                title: point.count > 1 ? `${point.count}个点` : (point.names[0] || ''),
                offset: new window.AMap.Pixel(-7, -7),
                content: markerContent
              })
              // 保存合并后的数据到 marker 的 extData
              marker.setExtData({
                count: point.count,
                totalValue: point.totalValue,
                names: point.names
              })
              return marker
            })
            
            markersRef.current = markers

            const createClusterRenderer = () => (ctx) => {
              const count = ctx.count
              // 计算聚合点的总权重值
              let totalValue = 0
              let allNames = []
              if (ctx.markers && Array.isArray(ctx.markers)) {
                ctx.markers.forEach((m) => {
                  const extData = m.getExtData?.() || {}
                  totalValue += extData.totalValue || 0
                  if (extData.names && Array.isArray(extData.names)) {
                    allNames.push(...extData.names)
                  }
                })
              }
              
              let size = 28
              let fontSize = 12
              if (count > 100) {
                size = 48
                fontSize = 16
              } else if (count > 50) {
                size = 40
                fontSize = 14
              } else if (count > 20) {
                size = 34
                fontSize = 13
              }
              
              const bg = count > 100 ? 'rgba(229,57,53,0.95)' 
                : count > 50 ? 'rgba(251,140,0,0.95)' 
                : count > 20 ? 'rgba(255,193,7,0.95)' 
                : 'rgba(30,136,229,0.95)'
              const border = 'rgba(200,230,255,0.95)'
              
              ctx.marker.setContent(
                `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:2px solid ${border};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;font-size:${fontSize}px;box-shadow:0 0 16px rgba(79,195,247,0.7);cursor:pointer;" title="${count}个点">${count}</div>`
              )
              
              // 设置聚合点的扩展数据
              ctx.marker.setExtData({
                count,
                totalValue,
                names: allNames
              })
            }

            if (typeof window.AMap?.MarkerCluster === 'function') {
              // JSAPI v2.0 - 减小 gridSize 让重叠的点更容易聚合
              const cluster = new window.AMap.MarkerCluster({
                map,
                markers,
                gridSize: 60, // 从80减小到60，让更近的点能够聚合
                maxZoom: 18, // 最大缩放级别，超过此级别不再聚合
                renderClusterMarker: createClusterRenderer()
              })
              clusterRef.current = cluster
            } else if (typeof window.AMap?.MarkerClusterer === 'function') {
              // JSAPI v1.4.x 兼容
              const cluster = new window.AMap.MarkerClusterer(map, markers, {
                gridSize: 60, // 从80减小到60
                maxZoom: 18,
                renderClusterMarker: createClusterRenderer()
              })
              clusterRef.current = cluster
            } else {
              // 无聚合插件则直接落点
              if (markers.length) map.add(markers)
            }
          } catch (e) {
            console.error('点聚合初始化失败:', e)
          }
        }

        const project = (p) => {
          const pixel = map.lngLatToContainer(p.coord)
          // 返回像素坐标 x/y（echarts 的 y 轴默认向上，这里使用 inverse:true）
          return [pixel.x, pixel.y, p.value, p.name]
        }

        let rafId = null
        let pending = false
        const update = () => {
          if (pending) return
          pending = true
          rafId = window.requestAnimationFrame(() => {
            // 依据当前地图视图，将经纬度投影为像素并刷新 ECharts
            if (!chart || !map) return
            const width = overlayRef.current.clientWidth
            const height = overlayRef.current.clientHeight

            const data = points.map(project)

            const option = {
              backgroundColor: 'transparent',
              animation: false,
              tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(5, 28, 43, 0.95)',
                borderColor: 'rgba(79, 195, 247, 0.5)',
                borderWidth: 1,
                textStyle: {
                  color: '#c8e6ff',
                  fontSize: 12
                },
                formatter: (params) => {
                  if (params && params.value) {
                    const v = params.value
                    const level = v[2] > 80 ? '高' : v[2] > 60 ? '中高' : v[2] > 40 ? '中' : '低'
                    return `
                      <div style="padding: 8px;">
                        <div style="color: #29b6f6; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid rgba(79,195,247,0.3); padding-bottom: 4px;">
                          📍 ${v[3]}
                        </div>
                        <div style="color: #aee2ff; margin-bottom: 4px;">热力等级: <span style="color: ${level === '高' ? '#ff7043' : level === '中高' ? '#29b6f6' : '#4fc3f7'};">${level}</span></div>
                        <div style="color: #aee2ff; margin-bottom: 4px;">权重值: <span style="color: #ffd54f;">${v[2]}</span></div>
                        <div style="color: #4fc3f7; font-size: 11px; margin-top: 8px; border-top: 1px solid rgba(79,195,247,0.2); padding-top: 4px;">
                          经度: ${v[0].toFixed ? v[0].toFixed(4) : v[0]}°<br/>
                          纬度: ${v[1].toFixed ? v[1].toFixed(4) : v[1]}°
                        </div>
                      </div>
                    `
                  }
                  return ''
                }
              },
              // 直角坐标系范围与 overlay 容器宽高绑定
              xAxis: { show: false, min: 0, max: width },
              yAxis: { show: false, min: 0, max: height, inverse: true },
              visualMap: {
                show: false,
                min: 0,
                max: 100,
                right: 20,
                top: '15%',
                calculable: false,
                textStyle: { 
                  color: '#c8e6ff',
                  fontSize: 11
                },
                itemWidth: 15,
                itemHeight: 100,
                inRange: {
                  color: ['#ff7043', '#ff8a65', '#ffab91', '#ffccbc'],
                  symbolSize: [8, 28]
                },
                outOfRange: {
                  color: '#333'
                }
              },
              // 关闭散点渲染，避免与原生热力图/聚合视觉叠加
              series: []
            }
            chart.setOption(option)
            pending = false
          })
        }

        // 首次更新
        update()

        // 当地图视图变化时需要重新投影点
        const debounced = (() => {
          let tid = null
          return () => {
            if (tid) window.clearTimeout(tid)
            tid = window.setTimeout(update, 80)
          }
        })()
        map.on('moveend', debounced)
        map.on('zoomend', debounced)
        map.on('resize', debounced)

        // --- 矩形缩放（Shift + 拖拽）实现 ---
        // 在 map 容器上监听鼠标事件，按住 Shift 并左键拖拽可画框缩放到选定范围
        const container = mapContainerRef.current
        let isBox = false
        let startX = 0
        let startY = 0
        let boxEl = null

        const onMouseDown = (e) => {
          // 仅当按住 Shift 且左键时开始框选
          if (!e.shiftKey || e.button !== 0) return
          isBox = true
          const rect = container.getBoundingClientRect()
          startX = e.clientX - rect.left
          startY = e.clientY - rect.top
          boxEl = document.createElement('div')
          boxEl.style.position = 'absolute'
          boxEl.style.left = `${startX}px`
          boxEl.style.top = `${startY}px`
          boxEl.style.width = '0px'
          boxEl.style.height = '0px'
          boxEl.style.border = '2px dashed rgba(0,120,255,0.8)'
          boxEl.style.background = 'rgba(0,120,255,0.08)'
          boxEl.style.zIndex = 999
          container.appendChild(boxEl)
          // 阻止地图默认行为（避免与框选冲突）
          e.preventDefault()
        }

        const onMouseMove = (e) => {
          if (!isBox || !boxEl) return
          const rect = container.getBoundingClientRect()
          const curX = e.clientX - rect.left
          const curY = e.clientY - rect.top
          const x = Math.min(startX, curX)
          const y = Math.min(startY, curY)
          const w = Math.abs(curX - startX)
          const h = Math.abs(curY - startY)
          boxEl.style.left = `${x}px`
          boxEl.style.top = `${y}px`
          boxEl.style.width = `${w}px`
          boxEl.style.height = `${h}px`
        }

        const onMouseUp = async (e) => {
          if (!isBox) return
          isBox = false
          const rect = container.getBoundingClientRect()
          const endX = e.clientX - rect.left
          const endY = e.clientY - rect.top
          // 清理框元素
          if (boxEl && boxEl.parentNode) boxEl.parentNode.removeChild(boxEl)
          boxEl = null

          // 计算地理范围并设置地图视图
          try {
            const p1 = map.containerToLngLat([startX, startY])
            const p2 = map.containerToLngLat([endX, endY])
            const lngMin = Math.min(p1.lng || p1[0], p2.lng || p2[0])
            const lngMax = Math.max(p1.lng || p1[0], p2.lng || p2[0])
            const latMin = Math.min(p1.lat || p1[1], p2.lat || p2[1])
            const latMax = Math.max(p1.lat || p1[1], p2.lat || p2[1])
            // 使用 AMap.Bounds 并 setBounds
            if (window.AMap && typeof window.AMap.Bounds === 'function') {
              const sw = new window.AMap.LngLat(lngMin, latMin)
              const ne = new window.AMap.LngLat(lngMax, latMax)
              const bounds = new window.AMap.Bounds(sw, ne)
              map.setBounds(bounds)
            } else if (map.setBounds) {
              map.setBounds([ [lngMin, latMin], [lngMax, latMax] ])
            } else {
              // 回退：以矩形中心为中心，放大一级
              const centerLng = (lngMin + lngMax) / 2
              const centerLat = (latMin + latMax) / 2
              map.setCenter([centerLng, centerLat])
              map.setZoom(map.getZoom() + 1)
            }
          } catch (err) {
            // ignore
          }
        }

        container.addEventListener('mousedown', onMouseDown)
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)

        // 窗口尺寸变化时，resize echarts 并更新
        const onWindowResize = () => {
          chart.resize()
          update()
        }
        window.addEventListener('resize', onWindowResize)

        // 清理函数：解绑事件、移除图层并销毁实例，防止内存泄漏
        const cleanup = () => {
          window.removeEventListener('resize', onWindowResize)
          // 移除框选与地图事件
          container.removeEventListener('mousedown', onMouseDown)
          window.removeEventListener('mousemove', onMouseMove)
          window.removeEventListener('mouseup', onMouseUp)
          if (map) {
            // 清理所有多边形和标签
            if (polygonRef.current && Array.isArray(polygonRef.current)) {
              polygonRef.current.forEach(({ polygon, textMarker }) => {
                try {
                  map.remove(polygon)
                  map.remove(textMarker)
                } catch (e) {
                  // ignore
                }
              })
              polygonRef.current = null
            }
            // 移除热力图图层
            try {
              if (heatmapRef.current) {
                map.remove(heatmapRef.current)
                heatmapRef.current = null
              }
            } catch (e) {
              // ignore
            }
            // 移除聚合与标记
            try {
              if (clusterRef.current && typeof clusterRef.current.setMap === 'function') {
                clusterRef.current.setMap(null)
                clusterRef.current = null
              } else if (clusterRef.current && map.remove) {
                map.remove(clusterRef.current)
                clusterRef.current = null
              }
              if (markersRef.current && markersRef.current.length) {
                map.remove(markersRef.current)
                markersRef.current = []
              }
            } catch (e) {
              // ignore
            }
            try {
              map.off('complete', applyMapStyle)
            } catch (e) {
              // ignore
            }
            map.off('moveend', debounced)
            map.off('zoomend', debounced)
            map.off('resize', debounced)
            map.destroy()
          }
          if (chart) chart.dispose()
        }

        // 保持引用用于卸载
        mapRef.current.cleanup = cleanup
      } catch (err) {
        // 加载或初始化失败时在控制台提示
        // console.error('AMap load/init failed', err)
      }
    }

    const scheduleInit = () => {
      const idler = window.requestIdleCallback || ((cb) => setTimeout(() => cb({ timeRemaining: () => 50 }), 120))
      idler(() => { if (!isUnmounted) init() })
    }

    // 仅在容器可见时初始化（ IntersectionObserver ）
    const el = mapContainerRef.current
    if (el && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          observer.disconnect()
          scheduleInit()
        }
      }, { root: null, threshold: 0.05 })
      observer.observe(el)
    } else {
      // 回退：直接延迟初始化
      scheduleInit()
    }

    return () => {
      isUnmounted = true
      if (observer && observer.disconnect) observer.disconnect()
      try {
        if (mapRef.current && mapRef.current.cleanup) mapRef.current.cleanup()
      } catch (e) {
        // ignore
      }
    }
  }, [points, mapStyleProp, mapTheme])

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      minHeight: '300px', // 手机端最小高度，确保地图能显示
      position: 'relative', 
      borderRadius: 6, 
      overflow: 'hidden',
      paddingTop: '8px',
      paddingBottom: '8px',
      backgroundColor: embedBackground
    }}>
      <style>{`
        @keyframes heatmap-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.95;
            box-shadow: 0 0 12px rgba(255,107,67,0.9), 0 0 20px rgba(255,107,67,0.5);
          }
          50% {
            transform: scale(1.4);
            opacity: 1;
            box-shadow: 0 0 20px rgba(255,107,67,1), 0 0 35px rgba(255,107,67,0.8), 0 0 50px rgba(255,107,67,0.4);
          }
        }
        @keyframes heatmap-pulse-ring-1 {
          0% {
            transform: scale(1);
            opacity: 0.7;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
        @keyframes heatmap-pulse-ring-2 {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          100% {
            transform: scale(5);
            opacity: 0;
          }
        }
        @keyframes heatmap-pulse-ring-3 {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          100% {
            transform: scale(6);
            opacity: 0;
          }
        }
      `}</style>
      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '280px',
          opacity: mapCanvasOpacity,
          borderRadius: 6,
          backgroundColor: isDarkHud ? '#060a10' : undefined,
          // 部分 Key 下瓦片仍为浅色：压光 + 降饱和，与 HUD 一致（仅 mapTheme=dark）
          ...(isDarkHud
            ? { filter: 'brightness(0.68) contrast(1.1) saturate(0.82)' }
            : {}),
        }}
      />
      <div ref={overlayRef} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, minHeight: '280px', pointerEvents: 'none' }} />
      
      {/* 热力参数控制面板 */}
      {RENDER_MODE === 'heat' && (
        <div style={{ 
          position: 'absolute', 
          right: 12, 
          top: 12, 
          zIndex: 1000, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 8,
          minWidth: 200,
          background: 'rgba(5, 28, 43, 0.86)',
          padding: '10px 12px',
          borderRadius: '8px',
          border: '1px solid rgba(79,195,247,0.35)',
          boxShadow: '0 4px 18px rgba(41, 182, 246, 0.18)'
        }}>
          <div style={{ color: '#aee2ff', fontWeight: 'bold', marginBottom: 2 }}>热力设置</div>
          <label style={{ color: '#c8e6ff', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            半径
            <input
              type="range"
              min={10}
              max={80}
              defaultValue={heatOptionsRef.current.radius}
              onChange={(e) => applyHeatOptions({ radius: Number(e.target.value) })}
              style={{ flex: 1 }}
            />
          </label>
          <label style={{ color: '#c8e6ff', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            透明
            <input
              type="range"
              min={0.2}
              max={1}
              step={0.05}
              defaultValue={heatOptionsRef.current.opacityMax}
              onChange={(e) => applyHeatOptions({ opacityMax: Number(e.target.value) })}
              style={{ flex: 1 }}
            />
          </label>
          <label style={{ color: '#c8e6ff', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            最大值
            <input
              type="number"
              min={10}
              max={500}
              step={10}
              defaultValue={heatOptionsRef.current.max}
              onChange={(e) => applyHeatOptions({ max: Number(e.target.value) })}
              style={{ flex: 1, background: 'rgba(5, 28, 43, 0.6)', color: '#aee2ff', border: '1px solid rgba(79,195,247,0.25)', borderRadius: 4, padding: '2px 6px' }}
            />
          </label>
        </div>
      )}
    </div>
  )
}

export default HeatMapVisualization
